import type { Sprite } from '@/components/pixel/sprites'

export type SpritePath = {
    fill: string
    d: string
}

const pathCache = new WeakMap<Sprite, SpritePath[]>()

// One path per color, built from horizontal runs of the same pixel, so the art stays crisp at any size.
export function spritePaths(sprite: Sprite): SpritePath[] {
    const cached = pathCache.get(sprite)

    if (cached) return cached

    const byColor = new Map<string, string>()

    sprite.rows.forEach((row, y) => {
        let x = 0

        while (x < row.length) {
            const char = row[x]
            const color = sprite.palette[char]

            if (!color) {
                x++
                continue
            }

            let end = x + 1

            while (end < row.length && row[end] === char) end++

            byColor.set(color, `${byColor.get(color) ?? ''}M${x} ${y}h${end - x}v1h${x - end}z`)
            x = end
        }
    })

    const paths = Array.from(byColor, ([fill, d]) => ({ fill, d }))

    pathCache.set(sprite, paths)

    return paths
}

// A standalone square SVG document of a sprite, used for the favicon.
export function spriteSvg(sprite: Sprite): string {
    const size = Math.max(sprite.width, sprite.height)
    const offsetX = (size - sprite.width) / 2
    const offsetY = (size - sprite.height) / 2
    const paths = spritePaths(sprite).map((path) => `<path fill="${path.fill}" d="${path.d}"/>`).join('')

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${-offsetX} ${-offsetY} ${size} ${size}" shape-rendering="crispEdges">${paths}</svg>`
}
